import { BalanceCardSkeleton } from '@/components/skeletons/WalletPageSkeletons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/EmptyState';
import { SFIcon } from '@/components/ui/sf-icon';
import { Heading, Text } from '@/components/ui/typography';
import { devLog } from '@/lib/logger';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { useWalletBalances } from './hooks/useWallet';
import { NetworkGroupedBalances } from './NetworkGroupedBalances';
import { PortfolioHeader } from './PortfolioHeader';

interface BalanceCardProps {
  showActions?: boolean;
  onAddFunds?: () => void;
  onWithdraw?: () => void;
  onTransfer?: () => void;
}

export function BalanceCard({ showActions = true, onAddFunds, onWithdraw, onTransfer }: BalanceCardProps) {
  const { data: wallets, isLoading, error } = useWalletBalances();

  if (isLoading) return <BalanceCardSkeleton />;

  if (error) {
    return (
      <Card className={cn(semanticTokens.danger.border, semanticTokens.danger.bg, semanticTokens.motion.standard)}>
        <CardContent className={cn(semanticTokens.spacing.lg, "text-center", semanticTokens.danger.fg)}>
          <Heading level={4} className="mb-2">
            Failed to load wallet balances
          </Heading>
          <Text variant="md" color="muted">
            {error instanceof Error ? error.message : 'Please try again later.'}
          </Text>
        </CardContent>
      </Card>
    );
  }

  // Check if user has wallets at all
  const hasWallets = wallets && wallets.length > 0;

  // Flatten balances from all wallets, preserving wallet ID for navigation
  const balances = wallets?.flatMap(w =>
    w.balances.map(b => {
      const balanceAmount = parseFloat(b.balance || '0');
      const currencyUpper = b.currency.toUpperCase();

      // Calculate USD value: use usd_value if provided, otherwise convert balance to USD
      // Bridge API precision: apply truncation to whole cents
      let usdValue = 0;
      if (b.usd_value) {
        const rawUsdValue = parseFloat(b.usd_value);
        // Bridge API precision: truncate to whole cents
        usdValue = Math.floor(rawUsdValue * 100) / 100;
      } else if (balanceAmount > 0) {
        // Bridge API precision: truncate balance amount first
        const truncatedBalance = Math.floor(balanceAmount * 100) / 100;

        // For stablecoins (USDC, USDT, USDB, EURC, DAI, USDP), assume 1:1 with USD
        // For EURC, use approximate EUR to USD rate (0.93)
        if (['USDC', 'USDT', 'USDB', 'DAI', 'USDP'].includes(currencyUpper)) {
          usdValue = truncatedBalance; // 1:1 for USD-pegged stablecoins
        } else if (currencyUpper === 'EURC') {
          usdValue = Math.floor((truncatedBalance * 0.93) * 100) / 100; // Approximate EUR to USD, truncate result
        } else if (currencyUpper === 'PYUSD') {
          usdValue = truncatedBalance; // PayPal USD is 1:1
        } else {
          // For other currencies, try to use balance as USD equivalent if reasonable
          // This is a fallback - ideally backend should provide usd_value
          usdValue = truncatedBalance;
        }
      }

      return {
        walletId: w.id,
        bridgeWalletId: w.bridge_wallet_id,
        currency: b.currency,
        amount: b.balance,
        chain: w.chain,
        usd_value: usdValue.toString(),
        address: w.address
      };
    })
  ) || [];

  // Calculate total USD value
  // Apply Bridge API precision: truncate to whole cents
  const totalUsdValue = balances.reduce(
    (sum, balance) => {
      const usdVal = parseFloat(balance.usd_value || '0');
      // Bridge API precision: truncate each balance to whole cents before summing
      const truncatedUsdVal = Math.floor(usdVal * 100) / 100;
      const result = sum + (isNaN(truncatedUsdVal) ? 0 : truncatedUsdVal);

      // Debug logging (temporary - remove after fixing)
      if (process.env.NODE_ENV === 'development') {
        devLog('[BalanceCard] Balance calculation:', {
          currency: balance.currency,
          amount: balance.amount,
          usd_value: balance.usd_value,
          parsed_usd: usdVal,
          truncated_usd: truncatedUsdVal,
          running_total: result
        });
      }

      return result;
    },
    0
  );

  // Final truncation to whole cents for total (Bridge API requirement)
  const finalTotalUsdValue = Math.floor(totalUsdValue * 100) / 100;

  // Debug logging (temporary - remove after fixing)
  if (process.env.NODE_ENV === 'development') {
    devLog('[BalanceCard] Final totals:', {
      balances_count: balances.length,
      total_usd_value: totalUsdValue,
      balances: balances.map(b => ({
        currency: b.currency,
        amount: b.amount,
        usd_value: b.usd_value
      }))
    });
  }

  // Determine wallet status
  const walletStatus = !hasWallets
    ? 'not_setup'
    : totalUsdValue > 0
      ? 'active'
      : 'ready';


  return (
    <div
      className={cn(
        "relative overflow-hidden",
        // Liquid Glass Surface tier (Hero card: rounded-[28px])
        "rounded-[28px]",
        "bg-gradient-to-br from-white/90 via-white/70 to-white/90",
        "dark:from-slate-900/80 dark:via-slate-800/65 dark:to-slate-900/80",
        "backdrop-blur-xl backdrop-saturate-[180%]",
        "dark:backdrop-saturate-150 dark:brightness-90",
        "border border-white/20 dark:border-white/10",
        "shadow-[0_8px_32px_hsl(220_15%_5%/0.12),inset_0_1px_0_hsl(0_0%_100%/0.8)]",
        "dark:shadow-[0_8px_32px_hsl(0_0%_0%/0.5),inset_0_1px_0_hsl(0_0%_100%/0.15)]",
        semanticTokens.motion.standard
      )}
    >
      {/* Specular highlight (top edge reflection) — Liquid Glass signature */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/20" />

      {/* Content container */}
      <div className="relative z-10">
        <Card className="border-0 bg-transparent shadow-none">
          <CardHeader>
            <div className={cn("flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4", semanticTokens.spacing.md.replace('p-', 'gap-'))}>
              <CardTitle asChild>
                <Heading level={3} className="flex items-center gap-2">
                  <SFIcon icon={sfIcons.wallet} className="w-5 h-5" aria-hidden="true" />
                  Wallet Balance
                </Heading>
              </CardTitle>

              {/* Quick Actions - Revolut-style: в header карточки */}
              {showActions && (onAddFunds || onWithdraw || onTransfer) && (
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {onAddFunds && (
                    <Button
                      variant="default"
                      size="sm"
                      className={cn("min-h-[44px] min-w-[100px] sm:min-w-[120px]")}
                      onClick={onAddFunds}
                      aria-label="Add funds to wallet"
                    >
                      <SFIcon icon={sfIcons.add} className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                      <span className="hidden sm:inline">Add Funds</span>
                      <span className="sm:hidden">Add</span>
                    </Button>
                  )}
                  {onWithdraw && (
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn("min-h-[44px] min-w-[100px] sm:min-w-[120px]")}
                      onClick={onWithdraw}
                      aria-label="Withdraw from wallet"
                    >
                      <SFIcon icon={sfIcons.arrowDownLeft} className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                      <span className="hidden sm:inline">Withdraw</span>
                      <span className="sm:hidden">Out</span>
                    </Button>
                  )}
                  {onTransfer && (
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn("min-h-[44px] min-w-[100px] sm:min-w-[120px]")}
                      onClick={onTransfer}
                      aria-label="Transfer funds"
                    >
                      <SFIcon icon={sfIcons.exchange} className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                      <span className="hidden sm:inline">Transfer</span>
                      <span className="sm:hidden">Move</span>
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardHeader>

          <CardContent className={cn("space-y-8 lg:space-y-12", semanticTokens.spacing.lg.replace('p-', 'space-y-'))}>
            {/* Portfolio Header */}
            <PortfolioHeader
              totalValue={finalTotalUsdValue}
              isLive={balances.length > 0}
            />

            {/* Network Grouped Balances - PRD-31 */}
            {!hasWallets ? (
              <EmptyState
                title="No wallet yet"
                description="Create a wallet to get started"
                variant="no-data"
                cta={{
                  label: "Add Funds",
                  onClick: onAddFunds || (() => { })
                }}
              />
            ) : !wallets || wallets.length === 0 ? (
              <EmptyState
                title="Wallet is empty"
                description="Add funds to get started"
                variant="no-data"
                cta={{
                  label: "Add Funds",
                  onClick: onAddFunds || (() => { })
                }}
              />
            ) : (
              <NetworkGroupedBalances
                wallets={wallets}
                onAddFunds={showActions ? onAddFunds : undefined}
                showZeroBalances={true}
              />
            )}

            {/* Quick Stats - Hidden as redundant with PortfolioHeader and Asset Cards */}
            {/* Removed to simplify UI and avoid duplication */}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
